// Flash banner — a one-shot confirmation toast surfaced from router state after
// a navigation (e.g. after an admin deletes a project / account and lands back
// on the list). There is no global toast system in this app; the house pattern
// is an in-page cream/brown banner (same grammar as ProjectDetailPage's reopen
// notice). The message travels in `location.state.flash` (see
// buildDeleteSuccessNav); on mount we clear that router state so a refresh or a
// browser Back doesn't replay the toast.
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, X } from "lucide-react";

export function FlashBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const flash = (location.state as { flash?: string } | null)?.flash ?? null;
  const [message, setMessage] = useState<string | null>(flash);

  useEffect(() => {
    if (!flash) return;
    setMessage(flash);
    // Drop the flash from router state so it doesn't replay on refresh / Back.
    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    });
  }, [flash, location.pathname, location.search, navigate]);

  if (!message) return null;

  return (
    <div className="mb-6 flex items-start justify-between gap-3 rounded-lg border border-brown-100 bg-cream-50 px-4 py-3 text-sm text-brown-500">
      <span className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-brown-400" aria-hidden />
        {message}
      </span>
      <button
        type="button"
        onClick={() => setMessage(null)}
        aria-label="Dismiss"
        className="rounded-md p-0.5 text-brown-300 hover:text-brown-500"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
